import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, MoreHorizontal, ListTree, GitBranch, Trash2 } from 'lucide-react';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { cn } from '@/core/lib/utils';
import { createChecklistItemTitleSchema } from '../schemas';
import type { ChecklistItem } from '../types';

interface ChecklistItemRowProps {
  item: ChecklistItem;
  canConvertToSubtask: boolean;
  onToggle: (itemId: string, isChecked: boolean) => void;
  onRename: (itemId: string, title: string) => void;
  onDelete: (itemId: string) => void;
  onConvertToTask: (itemId: string) => void;
  onConvertToSubtask: (itemId: string) => void;
}

export function ChecklistItemRow({
  item,
  canConvertToSubtask,
  onToggle,
  onRename,
  onDelete,
  onConvertToTask,
  onConvertToSubtask,
}: ChecklistItemRowProps) {
  const { t } = useTranslation('checklists');
  const rowRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(item.title);

  useEffect(() => {
    const el = rowRef.current;
    const handle = handleRef.current;
    if (!el || !handle) return;
    const cleanupDraggable = draggable({
      element: el,
      dragHandle: handle,
      getInitialData: () => ({ itemId: item.id, checklistId: item.checklistId }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });
    const cleanupDrop = dropTargetForElements({
      element: el,
      getData: () => ({ itemId: item.id, checklistId: item.checklistId, order: item.order }),
    });
    return () => {
      cleanupDraggable();
      cleanupDrop();
    };
  }, [item.id, item.checklistId, item.order]);

  function commitRename() {
    const parsed = createChecklistItemTitleSchema(t).safeParse(draft);
    if (parsed.success && parsed.data !== item.title) onRename(item.id, parsed.data);
    setIsEditing(false);
  }

  return (
    <div
      ref={rowRef}
      data-testid="checklists-item-row"
      className={cn(
        'group/item flex items-center gap-2 px-2 py-1.5 transition-opacity',
        isDragging && 'opacity-40',
      )}
    >
      <button
        type="button"
        ref={handleRef}
        data-testid="checklists-item-drag-handle"
        className="hidden cursor-grab touch-none text-muted-foreground active:cursor-grabbing md:block md:opacity-0 md:group-hover/item:opacity-100"
        aria-label={t('item.dragHandle')}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="size-4" />
      </button>

      <Checkbox
        checked={item.isChecked}
        onCheckedChange={(checked) => onToggle(item.id, checked === true)}
        aria-label={t('item.toggleAria', { title: item.title })}
        data-testid="checklists-item-checkbox"
      />

      {isEditing ? (
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            else if (e.key === 'Escape') {
              setDraft(item.title);
              setIsEditing(false);
            }
          }}
          className="h-7 flex-1 text-sm"
          data-testid="checklists-item-edit-input"
        />
      ) : (
        <button
          type="button"
          className={cn(
            'flex-1 truncate text-left text-sm',
            item.isChecked && 'text-muted-foreground line-through',
          )}
          onClick={() => {
            setDraft(item.title);
            setIsEditing(true);
          }}
        >
          {item.title}
        </button>
      )}

      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 opacity-0 transition-opacity group-hover/item:opacity-100 data-[state=open]:opacity-100"
                aria-label={t('item.openMenu')}
                data-testid="checklists-item-menu"
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('item.openMenu')}</TooltipContent>
        </Tooltip>

        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => onConvertToTask(item.id)}
            data-testid="checklists-item-convert-task"
          >
            <ListTree className="size-4" />
            {t('item.convertToTask')}
          </DropdownMenuItem>
          {canConvertToSubtask && (
            <DropdownMenuItem
              onClick={() => onConvertToSubtask(item.id)}
              data-testid="checklists-item-convert-subtask"
            >
              <GitBranch className="size-4" />
              {t('item.convertToSubtask')}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => onDelete(item.id)}
            data-testid="checklists-item-delete"
            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            <Trash2 className="size-4" />
            {t('item.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
